import { FC, useCallback, useEffect, useState } from 'react';

import { LockClosedIcon } from '@heroicons/react/outline';
import { phoneNumberStringFormator } from '@shared/utils/phoneNumberStringFormator';

interface PhoneLockEditModalProps {
  open: boolean;
  isSaving: boolean;
  /**
   * 현재 잠금되어 있는 번호 (있다면). 없으면 신규 잠금 등록 모드.
   */
  currentPhone?: string;
  /**
   * 현재 메모(받는 분 성함). 없으면 빈 값으로 시작.
   */
  currentMemo?: string;
  /**
   * 진열 시 사용자에게 보여줄 답례품 식별 정보 (예: "#3번째 답례품 · 스타벅스 e-쿠폰").
   * 없으면 일반 문구로 처리합니다.
   */
  itemLabel?: string;
  /**
   * 저장 콜백. 잠금/수정 모두 동일하게 호출됩니다 (백엔드 동일 endpoint).
   * `memo` 는 받는 분 성함으로, 비어 있으면 미전달.
   */
  onSave: (phoneNumber: string, memo?: string) => void;
  onClose: () => void;
}

/**
 * 답례품 수령자 휴대폰번호 잠금/수정 모달.
 *
 * - 잠금된 번호로만 본인 인증을 통해 수령이 가능 → 잘못 보낸 경우 안전망.
 * - 해제는 지원하지 않으며, 다른 번호로 변경만 가능합니다 (UX 결정).
 * - 동일한 번호로 저장 시도는 막아줍니다 (의미 없는 호출 방지).
 */
export const PhoneLockEditModal: FC<PhoneLockEditModalProps> = ({
  open,
  isSaving,
  currentPhone,
  currentMemo,
  itemLabel,
  onSave,
  onClose,
}) => {
  const [phone, setPhone] = useState('');
  const [memo, setMemo] = useState('');
  const [showDetail, setShowDetail] = useState(false);

  const isEditMode = !!currentPhone;

  useEffect(() => {
    if (open) {
      setPhone(currentPhone ?? '');
      setMemo(currentMemo ?? '');
      setShowDetail(false);
    }
  }, [open, currentPhone, currentMemo]);

  const handleClose = useCallback(() => {
    if (isSaving) return;
    onClose();
  }, [isSaving, onClose]);

  const handleSubmit = useCallback(() => {
    const sanitized = phone.replace(/[^0-9]/g, '');
    if (sanitized.length < 10) {
      window.alert('휴대폰번호를 정확히 입력해 주세요.');
      return;
    }
    if (isEditMode && sanitized === currentPhone) {
      window.alert('지금 지정된 번호와 같아요. 다른 번호를 입력해 주세요.');
      return;
    }
    const trimmedMemo = memo.trim();
    onSave(sanitized, trimmedMemo ? trimmedMemo : undefined);
  }, [phone, memo, isEditMode, currentPhone, onSave]);

  if (!open) return null;

  const sanitizedPhone = phone.replace(/[^0-9]/g, '');
  const isValid = sanitizedPhone.length >= 10;
  const isSamePhone = isEditMode && sanitizedPhone === currentPhone;
  const canSubmit = isValid && !isSamePhone && !isSaving;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6"
      onClick={handleClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-100">
              <LockClosedIcon className="h-5 w-5 text-amber-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-800">
              {isEditMode ? '받는 분 변경' : '받는 분 지정'}
            </h3>
          </div>
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 active:bg-gray-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>

        {itemLabel && (
          <p className="mb-3 truncate text-xs text-gray-500">{itemLabel}</p>
        )}

        {isEditMode && (
          <div className="mb-3 rounded-lg bg-gray-50 px-3 py-2">
            <p className="text-[11px] text-gray-500">지금 지정된 번호</p>
            <p className="text-sm font-medium text-gray-800">
              {phoneNumberStringFormator(currentPhone)}
            </p>
          </div>
        )}

        <p className="mb-1 break-keep text-sm leading-relaxed text-gray-700">
          입력한 번호의 받는 분만 이 답례품을 받을 수 있어요.
        </p>
        <p className="mb-2 break-keep text-[12px] leading-relaxed text-amber-700">
          번호가 틀리면 받으실 분도 받기 어려워져요. 한 번 더 확인해 주세요.
        </p>
        <button
          type="button"
          onClick={() => setShowDetail((v) => !v)}
          className="mb-3 text-[11px] text-gray-400 underline"
        >
          {showDetail ? '접기' : '자세히 보기'}
        </button>
        {showDetail && (
          <ul className="mb-4 list-disc space-y-1 break-keep rounded-lg bg-gray-50 px-3 py-2 pl-6 text-[11px] leading-relaxed text-gray-500">
            <li>지정된 번호로 본인 인증을 마친 분만 답례품을 받을 수 있으며, 링크가 다른 분께 전달돼도 사용되지 않습니다.</li>
            <li>지정 해제는 되지 않고 번호 변경만 가능합니다. 받는 분이 본인 인증을 마친 뒤에는 변경할 수 없습니다.</li>
            <li>유효기간 만료 전 안내와, 만료 후 환불(신유형 상품권 표준약관에 따라 구매금액의 90%) 안내는 지정된 번호로 전달될 수 있습니다.</li>
            <li>받는 분 성함은 전달 시 참고용이며 인증에는 사용되지 않습니다.</li>
          </ul>
        )}

        <div className="mb-4 flex flex-col gap-2">
          <input
            type="tel"
            inputMode="numeric"
            value={phoneNumberStringFormator(phone)}
            onChange={(e) => setPhone(e.target.value.replace(/[^0-9]/g, ''))}
            placeholder="받는 분 휴대폰번호"
            maxLength={13}
            className="w-full rounded-lg border border-amber-300 px-3 py-3 text-base focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
          <input
            type="text"
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="받는 분 성함 (선택)"
            maxLength={30}
            className="w-full rounded-lg border border-amber-300 px-3 py-3 text-base focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="w-2/5 rounded-xl border border-gray-300 py-3 text-sm font-medium text-gray-600 active:bg-gray-50"
          >
            닫기
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={`w-3/5 rounded-xl bg-amber-600 py-3 text-sm font-bold text-white active:bg-amber-700 ${
              !canSubmit ? 'opacity-50' : ''
            }`}
          >
            {isSaving ? '저장 중...' : isEditMode ? '변경하기' : '지정하기'}
          </button>
        </div>
      </div>
    </div>
  );
};
